'use client'
import { Button } from '@/components/ui/button/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog/dialog'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group/toggle-group'
import { env } from '@/env/client'
import { Schema } from '@/lib/fetching/schema'
import { BookText, ChevronRight } from 'lucide-react'
import React, { useState } from 'react'
import Image from 'next/image'
import { WowJourneyDialog } from './wow-journey-dialog'
import { setTrackerCookie } from '@/lib/common-utils'
import Link from 'next/link'
import { logger } from '@/utils/logger'

const CARD_DIALOG_CUTOFF = env.NEXT_PUBLIC_FORMS_TO_DISPLAY_PER_CARD

// ========================================== HELPER FUNCTIONS =========================================================

const formattedCount = (categorySize: number) =>
  categorySize.toString().padStart(2, '0')

// ========================================== DIALOG COMPONENT =========================================================

interface InstructionPopupProps {
  categoryName: string
  instructionList: Schema[]
  isStaff: boolean
  source_type?: string
  Lg?: string
  Lc?: string
}
const InstructionPopup: React.FC<InstructionPopupProps> = ({
  categoryName,
  instructionList,
  isStaff,
  source_type,
  Lg,
  Lc,
}) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="linkHover2"
          className="font-semibold text-blue-700 hover:text-blue-900"
          transactionName="forms.list.view_more_forms"
        >
          +{instructionList.length - CARD_DIALOG_CUTOFF} more forms
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] md:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {categoryName} ({instructionList.length})
          </DialogTitle>
        </DialogHeader>
        <div>
          <InstructionLink
            instructionList={instructionList}
            isStaff={isStaff}
            source_type={source_type}
            Lg={Lg}
            Lc={Lc}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ========================================== LINK  =========================================================
interface InstructionLinkProps {
  instructionList: Schema[]
  limit?: boolean
  isStaff: boolean
  source_type?: string
  Lg?: string
  Lc?: string
}
// TO-DO: Add Support to Link to Wow Journey when journey arrives
const ImageIcon: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  return src ? (
    <Image
      src={`data:image/svg+xml;base64,${src}`}
      alt={alt}
      width={30}
      height={30}
    />
  ) : (
    <BookText name="image" width={30} height={30} />
  )
}

const getLink = (
  schema: Schema,
  isStaff: boolean,
  source_type?: string,
  Lg?: string,
  Lc?: string
) => {
  if (schema.formType === 'digital') {
    if (isStaff) {
      return `/forms/${schema.id}`
    }

    const params = new URLSearchParams()
    params.append('formId', String(schema.id))

    if (Lg) params.append('Lg', Lg)
    if (Lc) params.append('Lc', Lc)
    if (source_type) params.append('source_type', source_type)

    return `/login/prerequisite?${params.toString()}`
  }

  if (schema.category === 'Digital Forms') {
    return `/digital-forms/${schema.id}`
  }

  return `/forms/${schema.id}`
}

const handleLinkClick = (formId: string) => {
  try {
    /** Set trackerId cookie for 10 minutes
     * reason for doing this is beacuse currently we have CUSTOMER_EXPIRY_MINUTES env variable,
     * which is server env and this component is client component
     */
    setTrackerCookie(formId, 10)
  } catch (err) {
    logger.error({ message: 'Failed to set tracker ID', error: err })
  }
}

const InstructionLink: React.FC<InstructionLinkProps> = ({
  instructionList,
  limit = false,
  isStaff,
  source_type,
  Lg,
  Lc,
}) => {
  return (
    <div className="flex max-h-[80vh] flex-col space-y-2 overflow-y-auto overflow-x-hidden pt-6">
      {instructionList
        .slice(0, limit ? CARD_DIALOG_CUTOFF : undefined)
        .map((schema) =>
          schema?.wowJourney?.length && isStaff ? (
            <div key={schema.id} data-transaction-name="wow-journey.open">
              <WowJourneyDialog schema={schema} />
            </div>
          ) : (
            <Link
              href={getLink(schema, isStaff, source_type, Lg, Lc)}
              key={schema.id}
              className="flex items-center justify-between rounded-lg p-2 font-semibold"
              data-transaction-name={`forms.open-${schema.id}`}
              onClick={() => handleLinkClick(schema.id)}
            >
              <div className="flex min-h-[32px] gap-1">
                <div className="mr-2 shrink-0">
                  <ImageIcon src={schema?.img?.data} alt={schema?.name} />
                </div>
                <div className="grow">
                  <p className="whitespace-normal text-[18px] font-semibold leading-7">
                    {schema.name}
                  </p>
                </div>
              </div>
              <ChevronRight className="size-6 shrink-0 text-blue-700 md:size-8" />
            </Link>
          )
        )}
    </div>
  )
}

// ========================================== INSTRUCTION CARD =========================================================

interface InstructionCardProps {
  category: string
  schemas: Schema[]
  isStaff: boolean
  source_type?: string
  Lg?: string
  Lc?: string
}

const InstructionCard: React.FC<InstructionCardProps> = ({
  category,
  schemas,
  isStaff,
  source_type,
  Lg,
  Lc,
}) => (
  <Card key={category} className="rounded-xl p-2">
    <CardHeader className="gap-4 pt-2 text-xl font-semibold leading-7">
      <CardHeader className="ml-4 gap-4 pt-2 text-xl font-semibold leading-7">
        {category} ({formattedCount(schemas.length)})
      </CardHeader>
    </CardHeader>
    <CardContent>
      <InstructionLink
        instructionList={schemas}
        limit={true}
        isStaff={isStaff}
        source_type={source_type}
        Lg={Lg}
        Lc={Lc}
      />
    </CardContent>
    {schemas.length > CARD_DIALOG_CUTOFF && (
      <CardFooter>
        <InstructionPopup
          categoryName={category}
          instructionList={schemas}
          isStaff={isStaff}
          source_type={source_type}
          Lg={Lg}
          Lc={Lc}
        />
      </CardFooter>
    )}
  </Card>
)

// ========================================== INSTRUCTION CARD GROUP =========================================================

interface InstructionCardGroupProps {
  schemasGroupedByCategory: Record<string, Schema[]>
  selected: string
  isStaff: boolean
  source_type?: string
  Lg?: string
  Lc?: string
}

const InstructionCardGroup: React.FC<InstructionCardGroupProps> = ({
  schemasGroupedByCategory,
  selected,
  isStaff,
  source_type,
  Lg,
  Lc,
}) => {
  let schemaList = Object.keys(schemasGroupedByCategory)
  let filteredCard = <></>

  if (selected !== 'all' && selected !== '') {
    schemaList = schemaList.filter((card) => card !== selected)
    filteredCard = (
      <InstructionCard
        category={selected}
        schemas={schemasGroupedByCategory[selected] ?? []}
        isStaff={isStaff}
        source_type={source_type}
        Lg={Lg}
        Lc={Lc}
      />
    )
  }
  return (
    <div className="mx-auto my-6 flex flex-col gap-8 overflow-hidden">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {filteredCard}
        {schemaList.map((c) => (
          <InstructionCard
            key={c}
            category={c}
            schemas={schemasGroupedByCategory[c] ?? []}
            isStaff={isStaff}
            source_type={source_type}
            Lg={Lg}
            Lc={Lc}
          />
        ))}
      </div>
    </div>
  )
}

// ========================================== BADGE COMPONENT =========================================================

interface InstructionBadgesProps {
  schemasGroupedByCategory: Record<string, Schema[]>
}
const InstructionBadges: React.FC<InstructionBadgesProps> = ({
  schemasGroupedByCategory,
}): React.JSX.Element => (
  <>
    {Object.keys(schemasGroupedByCategory).map((category) => (
      <ToggleGroupItem
        key={category}
        value={category}
        className="m-1 rounded-3xl border-blue-700 p-2 capitalize text-blue-600"
        variant="outline"
      >
        {category}
        <span className="hidden sm:inline">
          ({formattedCount(schemasGroupedByCategory[category]?.length ?? 0)})
        </span>
      </ToggleGroupItem>
    ))}
  </>
)

// ========================================== MAIN COMPONENT =========================================================
interface FormCardProps {
  schemasGroupedByCategory: Record<string, Schema[]>
  count: number
  isStaff: boolean
  source_type?: string
  Lg?: string
  Lc?: string
}

export const FormCards: React.FC<FormCardProps> = ({
  schemasGroupedByCategory,
  count,
  isStaff,
  source_type,
  Lg,
  Lc,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all')

  // TO-DO: Support Dynamic Icons, probably by transpiling

  return (
    <div className="m-3 gap-8 p-2 px-4">
      <ToggleGroup
        className="block justify-start"
        type="single"
        value={selectedCategory}
        onValueChange={setSelectedCategory}
      >
        <ToggleGroupItem value="all" className="rounded-3xl" variant="outline">
          All <span className="md:small">({formattedCount(count)})</span>
        </ToggleGroupItem>
        <InstructionBadges
          schemasGroupedByCategory={schemasGroupedByCategory}
        />
      </ToggleGroup>
      <InstructionCardGroup
        schemasGroupedByCategory={schemasGroupedByCategory}
        selected={selectedCategory}
        isStaff={isStaff}
        source_type={source_type}
        Lg={Lg}
        Lc={Lc}
      />
    </div>
  )
}
